/************************************************************************************************
 * FILE: ./components/common/Menu.js
 * LICENSE: MIT
 *
 * DESCRIPTION:
 *   - Navbar fijo con menú hamburguesa para versiones móviles (solo en Home).
 *   - En otras páginas, se muestra directamente el enlace (por ejemplo, Blog) sin menú hamburguesa.
 *   - El nombre de la aplicación redirige al inicio en cualquier página.
 *   - Modo dark (#121212) con acentos en #FF00AA.
 *   - Cumple principios KISS, DRY, SoC y los demás lineamientos.
 ************************************************************************************************/

'use client';

import React, { useState, useEffect } from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Box,
  IconButton,
  Typography,
  Button,
  useMediaQuery,
  Slide,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

// ----------------------------------------------------------------
// Constantes de configuración (Single Source of Truth)
// ----------------------------------------------------------------
const NAVBAR_HEIGHT = '60px';
const NAVBAR_BG = '#121212';
const NAVBAR_TEXT_COLOR = '#FFFFFF';
const NAVBAR_HOVER_COLOR = '#FF00AA';
const NAVBAR_LINK_FONT_SIZE = '1rem';
const NAVBAR_FONT_FAMILY = '"Work Sans", sans-serif';
const NAVBAR_ELEVATION = '0px 2px 8px rgba(0,0,0,0.4)';

// ----------------------------------------------------------------
// Estilos personalizados usando styled-components de MUI
// ----------------------------------------------------------------
const NavbarContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: 1000,
  width: '100%',
  backgroundColor: NAVBAR_BG,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: NAVBAR_HEIGHT,
  padding: '0 16px',
  transition: 'box-shadow 0.3s',
  userSelect: 'none',
  '& *': { userSelect: 'none' },
}));

// Este contenedor se oculta en mobile solo en Home, en otras páginas se muestra siempre
const LinksContainer = styled('nav')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const NavLink = styled('a')(() => ({
  textDecoration: 'none',
  color: NAVBAR_TEXT_COLOR,
  fontSize: NAVBAR_LINK_FONT_SIZE,
  fontWeight: 500,
  fontFamily: NAVBAR_FONT_FAMILY,
  position: 'relative',
  transition: 'color 0.3s',
  userSelect: 'none',
  '&:hover': { color: NAVBAR_HOVER_COLOR },
}));

const BurgerMenuContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
  },
}));

const MobileMenuOverlay = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  backgroundColor: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  justifyContent: 'flex-end',
  zIndex: 2000,
  userSelect: 'none',
  '& *': { userSelect: 'none' },
}));

const MobileMenuContainer = styled(Box)(() => ({
  width: '70%',
  maxWidth: '280px',
  height: '100%',
  backgroundColor: NAVBAR_BG,
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  userSelect: 'none',
  '& *': { userSelect: 'none' },
}));

const MobileNavLink = styled(NavLink)(() => ({
  marginBottom: '1rem',
}));

// ----------------------------------------------------------------
// Componente principal: Menu
// ----------------------------------------------------------------
export default function Menu() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNav, setShowNav] = useState(true);
  const [hasShadow, setHasShadow] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pathname = usePathname();
  const isHomePage = pathname === '/';

  // Secciones disponibles en el menú
  const sections = [
    { label: 'Manifiesto', href: '#por-que-elegirnos' },
    { label: 'Proyectos', href: '#proyectos-destacados' },
    { label: 'Metodología', href: '#metodologia-desarrollo' },
    { label: 'Stack Tecnológico', href: '#stack-tecnologico' },
    // Enlace a /blog que se mantiene en cualquier página
    { label: 'Blog', href: '/blog' },
  ];

  // Filtrar secciones: en Home se muestran todos, en otras páginas se omiten los enlaces de ancla.
  const filteredSections = sections.filter(
    (section) => isHomePage || !section.href.startsWith('#')
  );

  const handleToggleMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Controlar la visibilidad del navbar según scroll (ocultar al bajar, mostrar al subir)
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      setShowNav(window.scrollY <= lastScrollY || window.scrollY < 50);
      setHasShadow(window.scrollY > 10);
      lastScrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Slide appear={false} direction="down" in={showNav}>
      <NavbarContainer sx={{ boxShadow: hasShadow ? NAVBAR_ELEVATION : 'none' }}>
        {/* Logo o título que redirige a la página de inicio */}
        <NextLink href="/" passHref legacyBehavior>
          <Typography
            component="a"
            variant="h6"
            sx={{
              fontFamily: NAVBAR_FONT_FAMILY,
              fontWeight: 700,
              color: NAVBAR_TEXT_COLOR,
              fontSize: '1.5rem',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            FabricaDeApp
          </Typography>
        </NextLink>

        {/* En Home: se usa el contenedor responsive que oculta en mobile.
            En otras páginas: se fuerza a mostrar los enlaces (por ejemplo, Blog) */}
        <LinksContainer sx={!isHomePage ? { display: 'flex' } : {}}>
          {filteredSections.map((section, index) => (
            <NextLink key={index} href={section.href} passHref legacyBehavior>
              <NavLink>{section.label}</NavLink>
            </NextLink>
          ))}

          {/* Botón de CTA solo en Home */}
          {isHomePage && (
            <NextLink href="#contacto-rapido" passHref legacyBehavior>
              <Button variant="contained" sx={{ bgcolor: NAVBAR_HOVER_COLOR }}>
                Empezar
              </Button>
            </NextLink>
          )}
        </LinksContainer>

        {/* Menú hamburguesa solo en Home */}
        {isHomePage && (
          <BurgerMenuContainer>
            <IconButton onClick={handleToggleMenu} sx={{ color: NAVBAR_TEXT_COLOR }}>
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </BurgerMenuContainer>
        )}

        {/* Menú desplegable Mobile solo en Home */}
        {isHomePage && isMobile && mobileMenuOpen && (
          <MobileMenuOverlay onClick={handleToggleMenu}>
            <MobileMenuContainer onClick={(e) => e.stopPropagation()}>
              {filteredSections.map((section, index) => (
                <NextLink key={index} href={section.href} passHref legacyBehavior>
                  <MobileNavLink onClick={handleToggleMenu}>
                    {section.label}
                  </MobileNavLink>
                </NextLink>
              ))}
              {/* CTA en Mobile solo en Home */}
              {isHomePage && (
                <NextLink href="#contacto-rapido" passHref legacyBehavior>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: NAVBAR_HOVER_COLOR,
                      marginTop: 'auto',
                    }}
                    onClick={handleToggleMenu}
                  >
                    Empezar
                  </Button>
                </NextLink>
              )}
            </MobileMenuContainer>
          </MobileMenuOverlay>
        )}
      </NavbarContainer>
    </Slide>
  );
}
